const OnlineTaskStatus = require('@/lib/OnlineQueue/OnlineTaskStatus');
const OnlineTaskType = require('@/lib/OnlineQueue/OnlineTaskType');

/**
 * This is the heart of the offline order processing.
 *
 * OrderQueue was previously a big duplicate of OnlineQueue, the latter of which can handle queuing many different
 * types of tasks while the device was offline instead of just orders. OrderQueue is now far simpler and uses
 * OnlineQueue under the hood instead of being a separate implementation of the same thing.
 *
 * If the device is offline during checkout (or there is a failure when submitting the order) a createOrder task is
 * saved in the online queue which will have an OrderRequest in its data.
 */
class OrderQueue {
    /**
     * @param {OnlineQueue} onlineQueue
     * @param {OnlineTaskRepository} onlineTaskRepository
     */
    constructor(
        onlineQueue,
        onlineTaskRepository
    ) {
        this.onlineQueue = onlineQueue;
        this.onlineTaskRepository = onlineTaskRepository;
    }

    /**
     * @param {OrderRequest} request
     * @param {string} onlineTaskTrigger
     *
     * @return {Promise<OnlineTask>}
     */
    addRequest(request, onlineTaskTrigger) {
        const itemCount = request.reservation.lines
            ? Object.values(request.reservation.lines).reduce((totalQty, line) => totalQty + line.qty, 0)
            : 0;
        const task = this.onlineQueue.createTask(
            OnlineTaskType.CREATE_ORDER,
            {
                request,
            },
            `Create order for ${itemCount} item(s).`,
            onlineTaskTrigger
        );

        return this.onlineQueue.addTask(task);
    }

    /**
     * Returns all the OrderRequests contained within pending create order OnlineTasks.
     *
     * @return {Promise<OrderRequest[]>}
     */
    getQueuedOrders() {
        return this.onlineTaskRepository.findAllByType(OnlineTaskType.CREATE_ORDER)
            .then((tasks) => {
                return tasks.map((task) => {
                    return task.data.request;
                });
            });
    }

    /**
     * Returns the number of pending create order requests.
     *
     * @returns {Promise<number>}
     */
    getQueuedOrderCount() {
        return this.onlineTaskRepository
            .findAllByType(OnlineTaskType.CREATE_ORDER)
            .then((tasks) => tasks.filter((task) => task.status === OnlineTaskStatus.PENDING))
            .then((tasks) => tasks.length);
    }
}

module.exports = OrderQueue;
